import { Checkmark, ChevronDown } from '@carbon/icons-react';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  size as sizeMiddleware,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
} from '@floating-ui/react';
import { forwardRef, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@/shared/lib/cx';
import { mergeRefs } from '@/shared/lib/hooks';
import { useFieldAttributes, type ControlSize } from './Input';
import s from './Listbox.module.css';

export interface SelectOption<V extends string = string> {
  value: V;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface SelectProps<V extends string = string> {
  options: ReadonlyArray<SelectOption<V>>;
  value: V | null;
  onChange: (value: V) => void;
  placeholder?: string;
  size?: ControlSize;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  /** Доступное имя, если нет FormField. */
  'aria-label'?: string;
  /** Как показать выбранное значение в поле (по умолчанию — подпись опции). */
  renderValue?: (option: SelectOption<V>) => ReactNode;
  className?: string;
}

const listSizeMiddleware = sizeMiddleware({
  apply({ rects, elements, availableHeight }) {
    Object.assign(elements.floating.style, {
      minWidth: `${rects.reference.width}px`,
      maxHeight: `${Math.max(120, Math.min(availableHeight - 8, 360))}px`,
    });
  },
  padding: 8,
});

function SelectInner<V extends string>(
  { options, value, onChange, placeholder, size = 'md', disabled: disabledProp, invalid: invalidProp, id: idProp, className, renderValue, ...rest }: SelectProps<V>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const { t } = useTranslation('ui');
  const { id, invalid, disabled, describedBy, field } = useFieldAttributes({ id: idProp, invalid: invalidProp, disabled: disabledProp });
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);
  labelsRef.current = options.map((option) => option.label);
  const disabledIndices = useMemo(() => options.flatMap((option, index) => (option.disabled ? [index] : [])), [options]);

  const { refs, floatingStyles, context } = useFloating<HTMLButtonElement>({
    open,
    onOpenChange: (next) => {
      setOpen(next);
      if (next) setActiveIndex(selectedIndex >= 0 ? selectedIndex : null);
    },
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip({ padding: 8 }), listSizeMiddleware],
  });

  const select = (index: number) => {
    const option = options[index];
    if (!option || option.disabled) return;
    onChange(option.value);
    setOpen(false);
    refs.domReference.current?.focus();
  };

  const click = useClick(context, { event: 'mousedown', enabled: !disabled });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'select' });
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex: selectedIndex >= 0 ? selectedIndex : null,
    onNavigate: setActiveIndex,
    loop: true,
    disabledIndices,
    virtual: true,
  });
  const typeahead = useTypeahead(context, {
    listRef: labelsRef,
    activeIndex,
    selectedIndex: selectedIndex >= 0 ? selectedIndex : null,
    onMatch: (index) => {
      if (open) setActiveIndex(index);
      else if (options[index] && !options[index]?.disabled) onChange(options[index].value);
    },
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([click, dismiss, role, listNavigation, typeahead]);

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (open && activeIndex !== null && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      select(activeIndex);
    }
  };

  const ariaLabel = rest['aria-label'];
  const labelledBy = !ariaLabel && field ? field.labelId : undefined;

  return (
    <>
      <button
        ref={mergeRefs(ref, refs.setReference)}
        id={id}
        type="button"
        disabled={disabled}
        className={cx(s.trigger, size === 'sm' && s.triggerSm, size === 'lg' && s.triggerLg, className)}
        data-invalid={invalid ? 'true' : undefined}
        aria-describedby={describedBy}
        aria-label={ariaLabel}
        aria-labelledby={labelledBy}
        {...getReferenceProps({
          onKeyDown: handleKeyDown,
          /* floating-ui открывает список по mousedown и отменяет фокусировку: возвращаем фокус кнопке, чтобы после клика работала клавиатура. */
          onMouseDown: (event: ReactMouseEvent<HTMLButtonElement>) => event.currentTarget.focus({ preventScroll: true }),
          'aria-invalid': invalid || undefined,
        })}
      >
        <span className={cx(s.value, !selected && s.placeholder)}>
          {selected ? (renderValue ? renderValue(selected) : selected.label) : (placeholder ?? t('select.placeholder'))}
        </span>
        <ChevronDown size={16} className={s.chevron} aria-hidden="true" />
      </button>
      {open ? (
        <FloatingPortal>
          <div ref={refs.setFloating} className={s.popup} style={floatingStyles}>
            <ul className={s.list} {...getFloatingProps()}>
              {options.map((option, index) => (
                <li
                  key={option.value}
                  ref={(node) => {
                    listRef.current[index] = node;
                  }}
                  className={s.option}
                  data-active={activeIndex === index ? 'true' : undefined}
                  {...getItemProps({
                    active: activeIndex === index,
                    selected: option.value === value,
                    'aria-disabled': option.disabled || undefined,
                    onClick: () => select(index),
                  })}
                >
                  <span className={s.optionText}>
                    <span>{option.label}</span>
                    {option.description ? <span className={s.optionDescription}>{option.description}</span> : null}
                  </span>
                  {option.value === value ? <Checkmark size={16} className={s.check} aria-hidden="true" /> : null}
                </li>
              ))}
            </ul>
          </div>
        </FloatingPortal>
      ) : null}
    </>
  );
}

/** Выбор одного значения из списка (до ~15 вариантов; больше — Combobox с поиском). */
export const Select = forwardRef(SelectInner) as <V extends string = string>(
  props: SelectProps<V> & { ref?: React.Ref<HTMLButtonElement> },
) => ReturnType<typeof SelectInner>;
